<template>
	<div class="q-marketing-resources">
		<div class="q-marketing-resources__controls">
			<div class="q-marketing-resources__search">
				<v-text-field
					v-model="filters.search"
					prepend-icon="fas fa-search"
					hide-details
					class="q-marketing-resources__search-input"
					placeholder="Search by the file name, description or key word"
				/>
			</div>

			<v-row>
				<v-col cols="12" md="4">
					<v-select label="File Type" v-model="filters.type" :items="filtersVariants.types" clearable />
				</v-col>
				<v-col cols="12" md="4">
					<v-select label="Subject" v-model="filters.subject" :items="filtersVariants.subjects" clearable />
				</v-col>
				<v-col cols="12" md="4" class="d-flex justify-center align-center">
					<v-btn-toggle class="q-marketing-resources__audience-switch">
						<v-btn color="#0083EB" @click="toggleAudience('Agent')">Agent</v-btn>
						<v-btn color="#46C3B2" dark @click="toggleAudience('Customer')">Customer</v-btn>
					</v-btn-toggle>
				</v-col>
			</v-row>

			<v-divider class="mt-4" />
			<div style="height: 4px;"><v-progress-linear v-if="loading" indeterminate /></div>
		</div>

		<div v-if="!loading && !Object.keys(groupedItems).length">
			<p class="px-4 mt-6">No documents found</p>
		</div>

		<template v-else-if="!loading">
			<div class="my-4">
				<QButton depressed color="#1383eb" :disabled="!selectedItemsIDs.length || bulkDownloading" :loading="bulkDownloading" @click="downloadBulkSelected">Download Selected</QButton>
			</div>

			<template v-for="(items, group) in groupedItems">
				<div
					v-if="items.length > 0"
					:key="group"
					class="q-marketing-resources__group"
				>
					<h4 class="q-marketing-resources__group-title">{{ group }}</h4>
					<MarketingResourceTable
						:items="filterItems(items)"
						:selected="selected[group]"
						@update:selected="selected[group] = $event"
					/>
				</div>
			</template>
		</template>
	</div>
</template>

<script>
import { groupBy } from 'lodash'

import QuilityAPI from '@/store/API/QuilityAPI'
import MarketingResourceTable from '@/components/document_library/marketing_resource/Table.vue'
import QButton from '@/modules/shared/components/QButton.vue'

const WITHOUT_SUBJECTS = '- Without Subjects -'

export default {
  	name: 'MarketingResource',

  	components: { MarketingResourceTable, QButton },

	props: {
		contentData: { type: Object, required: true },
	},

	data () {
		return {
			data: [],
			loading: false,
			filters: {
				search: '',
				type: '',
				subject: '',
				audience: '',
			},
			selected: {},
			bulkDownloading: false,
		}
	},

	mounted () {
		this.loadData()
	},

	computed: {
		groups () {
			return this.contentData.groups || []
		},
		subjects () {
			return this.contentData.tags || []
		},
		filteredData () {
			return this.data
		},
		groupedItems () {
			if (!this.filteredData.length) { return {} }
			return groupBy(this.filteredData, 'SubCategory')
		},
		selectedItemsIDs () {
			return Object.values(this.selected).flat().map((item) => item.ID)
		},
		filtersVariants () {
			const types = this.filteredData
				.filter(({ DocumentTags }) => {
					if (!this.filters.subject) { return true }
					if (this.filters.subject === WITHOUT_SUBJECTS && !DocumentTags.length) { return true }
					return DocumentTags.includes(this.filters.subject)
				})
				.map(({ DocType }) => DocType)

			const filteredByDocType = this.filteredData
				.filter(({ DocType }) => this.filters.type ? DocType === this.filters.type : true)
			const flatTags = filteredByDocType.map(({ DocumentTags }) => DocumentTags).flat()
			const hasWithoutDocumentTags = filteredByDocType.find(({ DocumentTags }) => !DocumentTags.length)
			const uniqueTags = [...new Set(flatTags)].sort()
			const tags = hasWithoutDocumentTags ? [WITHOUT_SUBJECTS, ...uniqueTags] : uniqueTags
			const subjects = this.subjects.length
				? tags.filter((subject) => this.subjects.includes(subject))
				: tags

			return {
				types: [...new Set(types)].sort(),
				subjects,
			}
		},
        watcherCasheKey() {
            return this.groups.join('') + this.subjects.join('')
        },
	},

    watch: {
        watcherCasheKey() {
            this.loadData()
        },
		contentData () {
			this.loadData()
		},
		groupedItems () {
			this.formSelected()
		},
	},

	methods: {
		async loadData () {
			this.loading = true
			const response = await QuilityAPI.getMarketingResourceLibrary({
				SubCategory: this.groups.join(','),
				DocumentTags: this.subjects.join(',').replace(WITHOUT_SUBJECTS, '-empty-'),
			})
			if (response.error) {
				this.showError('Could not load Marketing Resource Library', response.msg)
			} else {
				this.data = response.data
			}
			this.loading = false
		},
		filterItems (items) {
			return items
				.filter(({ DocTitle, DocDesc }) => {
					return (
						DocTitle.toLowerCase().includes(this.filters.search.toLowerCase()) ||
						DocDesc?.toLowerCase().includes(this.filters.search.toLowerCase())
					)
				})
				.filter(({ DocumentTags }) => {
					if (!this.filters.subject) { return true }
					if (this.filters.subject === WITHOUT_SUBJECTS && !DocumentTags.length) { return true }
					return DocumentTags.includes(this.filters.subject)
				})
				.filter(({ DocType }) => {
					return this.filters.type ? DocType === this.filters.type : true
				})
				.filter(({ Audience }) => {
					return this.filters.audience ? Audience === this.filters.audience : true
				})
		},
		toggleAudience (to) {
			if (this.filters.audience === to) {
				this.filters.audience = ''
			} else {
				this.filters.audience = to
			}
		},
		formSelected () {
			const groups = Object.keys(this.groupedItems)
			this.selected = {}
			if (!groups.length) { return }
			groups.forEach(group => {
				this.$set(this.selected, group, [])
			})
		},
		downloadBulkSelected () {
			if (!this.selectedItemsIDs.length) { return }
			const params = {
				'itemsPerPage': 'zip',
				'DocumentIds': this.selectedItemsIDs.join(','),
			}
			const job = {
				'method': 'GET',
				'path': '/api/private/marketing_resource_library/bulk-download',
				'params': params,
				'JobName': 'MRL Export',
			}
			this.dispatchJob(job)
		},
	},
}
</script>

<style lang="scss">
.q-marketing-resources {
	border: 1px solid #ccc;
	padding: 1rem;

	&__controls {
		width: 100%;
		padding: 1rem 0;
	}

	&__search {
		max-width: 600px;
		margin: 0 auto 1rem;
	}

	&__search-input {
		width: 100%;
		padding-top: 0 !important;
		margin-top: 0 !important;
	}

	&__audience-switch {
		.v-btn__content {
			color: white !important;
		}

		&:has(.v-btn--active) {
			.v-btn:not(.v-btn--active) {
				opacity: 0.5 !important;
			}
		}

		.v-btn--active {
			font-weight: 800 !important;
		}
	}

	&__group {
		margin-top: 1.5rem;
	}

	&__group-title {
		padding: 0 1rem;
		font-size: 18px;
		font-weight: 600 !important;
	}

	.v-input {
		input {
			&::-webkit-input-placeholder, &:-moz-placeholder, &:-ms-input-placeholder, &::-ms-input-placeholder, &:placeholder-shown, &::placeholder { /* Safari, Chrome and Opera */
				opacity: 0.8;
			}
		}
	}
}
</style>